import {
	Abilities,
	ArmorType,
	Conditions,
	DamageTypes,
	Senses,
	Sizes,
	Skills,
	SpellSchools,
	WeaponCategories,
} from '../../../../components/enums';
import {ItemSubTypeNames, ItemTypeNames} from '../../../../components/render-helpers/item-type-names';
import {uppercaseFirstLetter, wordByCount} from '../../../../components/utils';
import type {Recharge, Sense} from './types';

const WEAPON_PROFICIENCIES: Record<string, string> = {
	Simple: 'Простое оружие',
	Martial: 'Военное оружие',
	LightMartial: 'Военное оружие со свойством Лёгкое',
	FencingMartial: 'Военное оружие со свойством Фехтовальное',
	improvised: 'Импровизированное оружие',
};

const WEAPON_MASTERY_LABELS: Record<string, string> = {
	Nick: 'Быстрое',
	Graze: 'Задевающее',
	Slow: 'Замедляющее',
	Topple: 'Опрокидывающее',
	Sap: 'Ослабляющее',
	Vex: 'Отвлекающее',
	Push: 'Отталкивающее',
	Cleave: 'Рассекающее',
};

const WEAPON_PROPERTY_LABELS: Record<string, string> = {
	Ammunition: 'Боеприпасы',
	AmmunitionFirearms: 'Боеприпасы (огнестрельные)',
	BurstFire: 'Очередь',
	Finesse: 'Фехтовальное',
	Heavy: 'Тяжёлое',
	Light: 'Лёгкое',
	Loading: 'Перезарядка',
	Reach: 'Досягаемость',
	Reload: 'Зарядка',
	Special: 'Особое',
	Thrown: 'Метательное',
	TwoHand: 'Двуручное',
	TwoHandUnlessMounted: 'Двуручное (кроме верхом)',
	Versatile: 'Универсальное',
};

const SENSE_LABELS: Record<Sense, string> = {
	...Senses,
	tremorsense: 'Чувство вибрации',
} as Record<Sense, string>;

const RECHARGE_LABELS: Record<Recharge, string> = {
	shortRest: 'короткий отдых',
	longRest: 'долгий отдых',
	shortOrLongRest: 'короткий или долгий отдых',
	dawn: 'рассвет',
};

export const abilityLabel = (ability: string) => Abilities[ability] || ability;

const ABILITY_SHORT_LABELS: Record<string, string> = {
	Strength: 'СИЛ',
	Dexterity: 'ЛВК',
	Constitution: 'ВЫН',
	Intelligence: 'ИНТ',
	Wisdom: 'МДР',
	Charisma: 'ХАР',
};

/** сокращение характеристики, как в блоке параметров: «МДР» */
export const abilityShortLabel = (ability: string) => ABILITY_SHORT_LABELS[ability] || abilityLabel(ability);

/** испытание со сложностью: «МДР 15» */
export const saveLabel = (abilities: string[], dc: number) => `${abilities.map(abilityShortLabel).join('/')} ${dc}`;

const CASTING_TIME_SHORT: Record<string, string> = {
	Action: 'Д',
	Bonus: 'БД',
	Reaction: 'ОД',
	Minute: 'М',
	Hour: 'Ч',
};

const CASTING_TIME_TITLES: Record<string, [string, string, string]> = {
	Action: ['действие', 'действия', 'действий'],
	Bonus: ['бонусное действие', 'бонусных действия', 'бонусных действий'],
	Reaction: ['ответное действие', 'ответных действия', 'ответных действий'],
	Minute: ['минута', 'минуты', 'минут'],
	Hour: ['час', 'часа', 'часов'],
};

/** время сотворения кратко: «1 БД» — и полностью для подсказки: «1 бонусное действие» */
export function castingTimeLabel(unit: string, amount = 1): {short: string; title: string} {
	return {
		short: `${amount} ${CASTING_TIME_SHORT[unit] || unit}`,
		title: CASTING_TIME_TITLES[unit] ? wordByCount(amount, CASTING_TIME_TITLES[unit], true) : `${amount} ${unit}`,
	};
}
export const skillLabel = (skill: string) => Skills[skill] || skill;
export const damageTypeLabel = (damageType: string) => DamageTypes[damageType] || damageType;
export const conditionLabel = (condition: string) => Conditions[uppercaseFirstLetter(condition)] || condition;
export const armorLabel = (armor: string) => ArmorType[armor] || armor;
export const weaponLabel = (weapon: string) => WEAPON_PROFICIENCIES[weapon] || weapon;
export const weaponMasteryLabel = (mastery: string) => WEAPON_MASTERY_LABELS[mastery] || mastery;
export const weaponPropertyLabel = (property: string) => WEAPON_PROPERTY_LABELS[property] || property;
export const weaponCategoryLabel = (category: string) => WeaponCategories[category] || category;
export const itemTypeLabel = (type: string) => ItemTypeNames[type as keyof typeof ItemTypeNames]?.title || type;
export const itemSubTypeLabel = (subType: string) =>
	ItemSubTypeNames[subType as keyof typeof ItemSubTypeNames]?.title || subType;
export const sizeLabel = (size: string) => Sizes[size] || size;
export const senseLabel = (sense: Sense) => SENSE_LABELS[sense];
export const rechargeLabel = (recharge: Recharge) => RECHARGE_LABELS[recharge] || recharge;
export const spellSchoolLabel = (school: string) => SpellSchools[school] || school;
